// Badge — a small status / count / kind label, pure.
//
// A non-interactive inline label with a quiet tinted fill per tone
// (--radius-sm, --text-xs). Rebuilt from foundations v1 against the design
// vision (.claude/research/design/06): near-monochrome, no resting border — the tint IS
// the grouping, an edge is never painted.
//
// The public surface is a DISCRIMINATED UNION on `tone`, split by whether the
// tone carries a *status* meaning:
//   - status tones (success | warning | danger) REQUIRE `icon` at the type
//     level — colour is never the sole channel (WCAG SC 1.4.1). A green pill
//     saying "Passed" is fine for a sighted user until the green is the only
//     thing distinguishing it from red; the required icon (and the always-
//     present label text) is the redundant channel.
//   - label tones (neutral | accent | agent) take `icon` optionally and
//     default to `neutral`. accent/agent spend the one accent hue on the
//     agent's identity or a single highlighted kind (.claude/research/design/06 §1).
//
// The label text ALWAYS carries the meaning, so the leading icon is decorative
// and rendered `aria-hidden` — it reinforces, it does not name. The badge is a
// plain <span>: non-interactive, so it is exempt from the target-size floor
// and carries no :hover / :focus / role.
//
// Pure/props-driven: `tone` + `icon` pick the look and the a11y channel; the
// label arrives as `children`; every other span attribute passes through.

import type { HTMLAttributes, ReactNode } from "react";
import "./Badge.css";

/** Tones that encode a status — colour would otherwise be the sole channel,
 * so the type demands an icon alongside the always-present label. */
export type BadgeStatusTone = "success" | "warning" | "danger";
/** Tones that name a kind/count/identity — neutral by default; accent/agent
 * spend the one reserved hue. */
type BadgeLabelTone = "neutral" | "accent" | "agent";
export type BadgeTone = BadgeStatusTone | BadgeLabelTone;

type BadgeStatusProps = HTMLAttributes<HTMLSpanElement> & {
    readonly tone: BadgeStatusTone;
    /** Required: the redundant channel that keeps status off hue-alone. */
    readonly icon: ReactNode;
};

type BadgeLabelProps = HTMLAttributes<HTMLSpanElement> & {
    readonly tone?: BadgeLabelTone;
    readonly icon?: ReactNode;
};

export type BadgeProps = BadgeStatusProps | BadgeLabelProps;

export const Badge = ({
    tone = "neutral",
    icon,
    className,
    children,
    ...rest
}: BadgeProps) => (
    <span
        className={`bilda-badge bilda-badge--${tone}${
            className ? ` ${className}` : ""
        }`}
        {...rest}
    >
        {icon ? (
            <span className="bilda-badge__icon" aria-hidden="true">
                {icon}
            </span>
        ) : null}
        {children}
    </span>
);
